// ===================================================================
// CLIENT LIST PAGE
// ===================================================================
// src/pages/clients/ClientList.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Plus, Eye, Edit, Archive } from 'lucide-react';
import { clientService } from '../../services/clientService';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';

/**
 * Client list page with search, filtering, and pagination
 * Displays all clients with quick actions and status indicators
 */
const ClientList = () => {
  const [inputValue, setInputValue] = useState('');   // instant — controls the input
  const [searchTerm, setSearchTerm] = useState('');   // debounced — drives the API query
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const searchRef = useRef(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [confirmModal, setConfirmModal] = useState(null);
  
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  // Debounce inputValue → searchTerm (400ms), reset page on new search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch clients with search and pagination
  const { data: clientsData, isLoading, error, refetch } = useQuery({
    queryKey: ['clients', searchTerm, filterStatus, currentPage, sortBy, sortOrder],
    queryFn: () => clientService.getClients({
      search: searchTerm,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      page: currentPage,
      limit: 20,
      sortBy,
      sortOrder
    }),
    keepPreviousData: true,
    staleTime: 30000
  });

  /**
   * Handle client row click
   */
  const handleRowClick = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  /**
   * Handle client archiving
   */
  const handleArchiveClient = (e, clientId) => {
    e.stopPropagation();
    setConfirmModal({
      title: 'Archive Client',
      message: 'Are you sure you want to archive this client? This action can be reversed later.',
      confirmLabel: 'Archive',
      danger: true,
      onConfirm: async () => {
        try {
          await clientService.archiveClient(clientId);
          showSuccess('Client archived successfully');
          refetch();
        } catch (error) {
          showError('Failed to archive client');
        }
      }
    });
  };

  /**
   * Handle sorting
   */
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  /**
   * Get primary dossier number from client's dossiers array
   */
  const getPrimaryDossier = (client) => {
    if (client.dossiers && client.dossiers.length > 0) {
      return client.dossiers[0].dossier_number;
    }
    return null;
  };

  // Don't early-return with a spinner — it unmounts the input and loses focus.
  // Show inline loading instead (see below).

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading clients. Please try again.</p>
        <button 
          onClick={refetch} 
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const clients = clientsData?.data || [];
  const totalPages = clientsData?.pagination?.pages || 1;
  const totalClients = clientsData?.pagination?.total || 0;
  const isInitialLoading = isLoading && !clientsData && !inputValue;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600">
            {totalClients} clients total
          </p>
        </div>
        
        {hasPermission('clients:create') && (
          <Link
            to="/clients/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Client
          </Link>
        )}
      </div>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search input */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search clients by name, email, or dossier number..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {isLoading && inputValue && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Status filter */}
          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Client table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isInitialLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No clients found</p>
            {hasPermission('clients:create') && (
              <Link
                to="/clients/new"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first client
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table view */}
            <div className="hidden sm:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('first_name')}
                    >
                      Name
                      {sortBy === 'first_name' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Dossier Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortBy === 'status' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                      {sortBy === 'created_at' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.map((client) => (
                    <tr 
                      key={client.id} 
                      onClick={() => handleRowClick(client.id)}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {client.first_name?.charAt(0)}{client.last_name?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {client.personal_number || 'Not set'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPrimaryDossier(client) || (
                          <span className="text-gray-400 italic">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{client.email}</div>
                        <div>{client.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          client.status === 'active' 
                            ? 'bg-green-100 text-green-800'
                            : client.status === 'inactive'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {client.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(client.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/clients/${client.id}`);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {hasPermission('clients:update') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/clients/${client.id}/edit`);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {hasPermission('clients:delete') && (
                            <button
                              onClick={(e) => handleArchiveClient(e, client.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view */}
            <div className="sm:hidden">
              {clients.map((client) => (
                <div 
                  key={client.id} 
                  onClick={() => handleRowClick(client.id)}
                  className="px-4 py-4 border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {client.first_name?.charAt(0)}{client.last_name?.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {client.first_name} {client.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getPrimaryDossier(client) || 'No dossier'}
                        </div>
                      </div>
                    </div>
                    <Eye className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default ClientList;